import React, { ReactNode } from "react";
import classNames from "classnames";

import Columns from "./Columns";
import Rows from "./Rows";

export interface TableHeaderProps {
  key?: string;
  label: string | ReactNode;
  isSortable?: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (val: any, data: Record<string, any>) => string | any;
  className?: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalDOMAttributes?: Record<string, any>;
  columnIsSortedOn?: boolean;
  sortedOnCellClass?: string;
  testID?: string;
}

export interface TableCellProps {
  key?: string;
  labels: Array<string> | string | ReactNode;
  className?: string;
  testID?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface TableDataProps extends Record<string | number, any> {
  fields?: Array<TableCellProps>;
  className?: string;
  highlight?: boolean;
}

export interface TableProps {
  headers: ReadonlyArray<TableHeaderProps>;
  data: ReadonlyArray<TableDataProps>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get?: (key?: string, result?: Record<string, any>) => any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (v: Record<string, any>) => any;
  className?: string;
  showDifference?: boolean;
  highlightActive?: boolean;
  testIndex?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getForKey = (key?: string, data?: Record<string, any>): any => {
  const parts = key.split(".");
  let target = data;
  const last = parts.pop();
  for (const part of parts) {
    if (target[part]) {
      target = target[part];
    } else {
      return null;
    }
  }
  if (!target) {
    return null;
  }
  return target[last];
};

// default getter
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultGet = (key?: string, data?: Record<string, any>): any => {
  if (data) {
    return getForKey(key, data);
  } else {
    return "-";
  }
};

const shouldHighlightDifference = (data: ReadonlyArray<TableDataProps>, index: number, key: string): string => {
  const value = defaultGet(key, data[index]);
  let diffValue = defaultGet(key, data[index]);

  if (index < data.length - 1) {
    diffValue = defaultGet(key, data[index + 1]);
  }

  if (value !== diffValue) {
    return "highlight-difference";
  }

  return "";
};

const shouldHighlightActive = (data: TableDataProps): string => {
  if (data.highlight) {
    return "highlight-cell";
  }
  return "";
};

export const Table: React.FC<TableProps> = ({
  headers,
  data,
  get = defaultGet,
  onClick,
  className,
  showDifference,
  highlightActive,
  testIndex = "0",
}: TableProps): JSX.Element => {
  const formattedData = data.map((result, index) => {
    // this ensures labels are unique if multiple headers have the same key for some reason
    const existingKeys: string[] = [];
    return {
      ...result,
      fields: headers.map((header, j) => {
        let labels = get(header.key, result);

        // format label if formatter exists
        if (typeof header.formatter === "function" && labels !== null) {
          labels = header.formatter(labels, result);
        }

        // apply sorted class
        let cellClassName = "";
        if (header.columnIsSortedOn) {
          cellClassName = header.sortedOnCellClass;
        }

        if (!labels) {
          labels = "-";
          const centerTextClass = "caprx-h-td-center-text";
          cellClassName = `${cellClassName || ""} ${centerTextClass}`;
        }

        if (showDifference) {
          const shouldHighlightDifferenceClass = shouldHighlightDifference(data, index, header.key);
          cellClassName = `${cellClassName || ""} ${shouldHighlightDifferenceClass || ""}`;
        }

        if (highlightActive) {
          const shouldHighlightClass = shouldHighlightActive(result);
          cellClassName = `${cellClassName || ""} ${shouldHighlightClass || ""}`;
        }

        if (header.className) {
          cellClassName = `${cellClassName || ""} ${header.className}`;
        }

        let headerLabel = j.toString(10);
        if (header.key && !existingKeys.includes(header.key)) {
          headerLabel = header.key;
          existingKeys.push(header.key);
        }

        return {
          key: j.toString(),
          className: cellClassName,
          testID: "test__table-" + testIndex + "-row-" + index + "-field-" + headerLabel,
          labels,
        };
      }),
      onClick,
    };
  });

  // this ensures labels are unique if multiple headers have the same key for some reason
  const existingKeys: string[] = [];
  const newHeaders = headers.map((header, index) => {
    let headerLabel = index.toString(10);
    if (header.key && !existingKeys.includes(header.key)) {
      headerLabel = header.key;
      existingKeys.push(header.key);
    }

    return {
      ...header,
      testID: "test__table-" + testIndex + "-header-" + headerLabel,
    };
  });

  return (
    <table className={classNames("caprx-table", className)} data-testid={"test__table-" + testIndex}>
      <thead>
        <Columns headers={newHeaders} />
      </thead>
      <tbody>
        <Rows data={formattedData} onClick={onClick} rowTestID={"test__table-" + testIndex + "-row-"} />
      </tbody>
    </table>
  );
};

export default Table;
